import {
  type BlocksContent,
  BlocksRenderer,
} from "@strapi/blocks-react-renderer";
import clsx from "clsx";
import type { ComponentProps, ReactNode } from "react";

import type { ConditionsTableType } from "@features/article-content/model";

import { Image } from "@shared/ui/image";
import { Body, Heading } from "@shared/ui/typography";
import { restoreRelativeUrl } from "@shared/utils";

import s from "./conditions-table.module.scss";

type ConditionsTableProps = ComponentProps<"section"> & ConditionsTableType;

type AnyBlock = { type?: string; image?: unknown } & Record<string, unknown>;

const groupImagesIntoRows = (content: AnyBlock[]) => {
  const result: AnyBlock[] = [];
  let buffer: AnyBlock[] = [];

  const flush = () => {
    if (buffer.length === 0) return;
    if (buffer.length === 1) {
      result.push(buffer[0]);
    } else {
      result.push({
        type: "images-row",
        images: buffer.map((b) => b.image),
        children: [],
      });
    }
    buffer = [];
  };

  for (const block of content) {
    if (block?.type === "image") {
      buffer.push(block);
      continue;
    }
    flush();
    result.push(block);
  }

  flush();
  return result;
};

export const ConditionsTable = (props: ConditionsTableProps) => {
  const { className, title, rows } = props;

  return (
    <table className={clsx(s.root, className)}>
      {title != null && title !== "" && (
        <caption>
          <Heading level={6} className={s.title}>
            {title}
          </Heading>
        </caption>
      )}
      <tbody>
        {rows?.map((row) =>
          row ? (
            <tr key={row?.id ?? row?.label ?? Math.random()} className={s.row}>
              <Body
                tag="td"
                weight="semiBold"
                className={clsx(s.cell, s.labelCell)}
              >
                {row?.label ?? ""}
              </Body>
              <Body
                tag="td"
                className={clsx(s.cell, s.valueCell)}
                data-label={row.label}
              >
                {row.value && (
                  <BlocksRenderer
                    content={
                      groupImagesIntoRows(
                        (row.value as unknown as AnyBlock[]) ??
                          ([] as AnyBlock[]),
                      ) as unknown as BlocksContent
                    }
                    blocks={
                      {
                        paragraph: ({ children }: { children: ReactNode }) => (
                          <Body className={s.text}>{children}</Body>
                        ),
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        "images-row": ({ images }: { images?: unknown }) => {
                          return (
                            <div className={s.imagesRow}>
                              {(images as unknown[])?.map((img, idx) => {
                                // biome-ignore lint/suspicious/noExplicitAny: Strapi blocks image shape is dynamic
                                const media = img as any;
                                const src = restoreRelativeUrl(
                                  media?.url ?? "",
                                );
                                if (!src) return null;

                                return (
                                  <Image
                                    key={`${src}-${idx + 1}`}
                                    className={s.image}
                                    src={src}
                                    alt={
                                      media?.alternativeText ||
                                      media?.name ||
                                      "table image"
                                    }
                                    objectFit="contain"
                                  />
                                );
                              })}
                            </div>
                          );
                        },
                        // biome-ignore lint/suspicious/noExplicitAny: BlocksRenderer image block props are not typed
                        image: ({ image }: any) => {
                          // biome-ignore lint/suspicious/noExplicitAny: Strapi blocks image shape is dynamic
                          const media = image as any;
                          const src = restoreRelativeUrl(media?.url ?? "");
                          if (!src) return null;

                          return (
                            <Image
                              className={s.image}
                              src={src}
                              alt={
                                media?.alternativeText ||
                                media?.name ||
                                "table image"
                              }
                              objectFit="contain"
                            />
                          );
                        },
                        link: ({
                          children,
                          url,
                        }: {
                          children: ReactNode;
                          url: string;
                        }) => {
                          const cleaned = restoreRelativeUrl(url);
                          return (
                            <a
                              href={cleaned}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={clsx(s.link, "global-link")}
                            >
                              {children}
                            </a>
                          );
                        },
                        list: ({
                          children,
                          format,
                        }: {
                          children: ReactNode;
                          format: "unordered" | "ordered";
                        }) => {
                          switch (format) {
                            case "unordered":
                              return (
                                <Body
                                  tag="ul"
                                  className={clsx(s.list, s.unordered)}
                                >
                                  {children}
                                </Body>
                              );
                            case "ordered":
                              return (
                                <Body
                                  tag="ol"
                                  className={clsx(s.list, s.ordered)}
                                >
                                  {children}
                                </Body>
                              );
                          }
                        },
                        // biome-ignore lint/suspicious/noExplicitAny: BlocksRenderer expects a complex blocks map type
                      } as any
                    }
                    modifiers={{
                      bold: ({ children }) => <strong> {children} </strong>,
                    }}
                  />
                )}
              </Body>
            </tr>
          ) : null,
        )}
      </tbody>
    </table>
  );
};

ConditionsTable.displayName = "ConditionsTable";
